import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import { Check, X, AlertTriangle, ArrowRight, CheckCircle2, XCircle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

export function SyncPreviewModal({ isOpen, onClose, previewData, onApply, title, confirmLabel, onSkip }) {
    const [decisions, setDecisions] = useState({}); // { itemId: 'accepted' | 'rejected' }
    const [loading, setLoading] = useState(false);
    const [showAutoChanges, setShowAutoChanges] = useState(false);

    // Reset decisions when modal opens with new data
    useEffect(() => {
        if (isOpen) {
            setDecisions({});
        }
    }, [isOpen, previewData]);

    // 1. Loading / No Data State (Guardrail)
    if (!previewData) {
        if (isOpen) {
            return (
                <Dialog open={isOpen} onOpenChange={onClose}>
                    <DialogContent className="flex flex-col items-center py-12">
                        <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
                        <p className="text-gray-500">Cargando...</p>
                    </DialogContent>
                </Dialog>
            );
        }
        return null;
    }

    // 2. Error State Handling
    if (previewData?.error) {
        return (
            <Dialog open={isOpen} onOpenChange={(open) => !loading && onClose(open)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <XCircle className="h-6 w-6" />
                            Error de Análisis
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            {previewData.message || "Ocurrió un error al analizar la marca."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-red-50 p-4 rounded-md text-sm text-red-800 my-2">
                        No se pudieron obtener los cambios desde el IMPI. Puedes reintentar o saltar esta marca.
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        {onSkip && (
                            <Button variant="ghost" onClick={onSkip} className="mr-auto text-gray-500 hover:text-gray-700">
                                Saltar Marca <ArrowRight className="ml-1 h-3 w-3" />
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => onClose(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={() => onApply([], [])} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Reintentar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    // P2 UX: "No Changes" State
    const totalChanges = previewData.total_changes ?? previewData.summary?.total ?? 0;

    if (totalChanges === 0) {
        return (
            <Dialog open={isOpen} onOpenChange={(open) => !loading && onClose(open)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-green-600">
                            <CheckCircle2 className="h-6 w-6" />
                            Marca Sincronizada
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            No hay actualizaciones en el sistema del IMPI para esta marca.
                            La información local ya es la más reciente.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-green-50 p-4 rounded-md text-sm text-green-800 my-2 flex items-center gap-3">
                        <div className="bg-white p-2 rounded-full shadow-sm">
                            <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span>{previewData.message || "Todo está al día."}</span>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={async () => {
                                if (onApply) {
                                    setLoading(true);
                                    try {
                                        await onApply([], []);
                                    } catch (e) { }
                                    setLoading(false);
                                } else {
                                    onSkip ? onSkip() : onClose(false);
                                }
                            }}
                            disabled={loading}
                            className="bg-gray-900 hover:bg-gray-800"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            {onSkip ? "Siguiente Marca" : "Aceptar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }



    if (!previewData) return null; // Redundant but safe

    const { items_requires_approval, items_auto_apply = [], summary, message } = previewData;
    const criticalCount = items_requires_approval.length;

    // Helpers
    const handleDecision = (id, decision) => {
        setDecisions(prev => ({ ...prev, [id]: decision }));
    };

    const handleAcceptAll = () => {
        const newDecisions = {};
        items_requires_approval.forEach(item => {
            newDecisions[item.id] = 'accepted';
        });
        setDecisions(newDecisions);
    };

    const handleRejectAll = () => {
        const newDecisions = {};
        items_requires_approval.forEach(item => {
            newDecisions[item.id] = 'rejected';
        });
        setDecisions(newDecisions);
    };

    const handleConfirm = async () => {
        const acceptedIds = [];
        const rejectedIds = [];

        console.log('[SYNC MODAL] items_requires_approval:', items_requires_approval);
        console.log('[SYNC MODAL] decisions:', decisions);

        items_requires_approval.forEach(item => {
            console.log(`[SYNC MODAL] Processing item: ${item.id}, entity: ${item.entity_type}:${item.field}, decision: ${decisions[item.id]}`);
            const decision = decisions[item.id];
            if (decision === 'accepted') {
                acceptedIds.push(item.id);
                console.log(`[SYNC MODAL] ✅ Added to accepted: ${item.id}`);
            }
            if (decision === 'rejected') {
                rejectedIds.push(item.id);
                console.log(`[SYNC MODAL] ❌ Added to rejected: ${item.id}`);
            }
        });

        console.log('[SYNC MODAL] Final acceptedIds:', acceptedIds);
        console.log('[SYNC MODAL] Final rejectedIds:', rejectedIds);

        setLoading(true);
        try {
            await onApply(acceptedIds, rejectedIds);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Render helpers
    const formatValue = (val) => {
        if (val === null || val === undefined) return <span className="text-gray-400 italic">Vacío</span>;
        if (typeof val === 'object') return JSON.stringify(val);
        if (val === '') return <span className="text-gray-400 italic">Cadena vacía</span>;
        return String(val);
    };

    // Calculate pending decisions
    const pendingCount = items_requires_approval.filter(item => !decisions[item.id]).length;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !loading && onClose(open)}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        {criticalCount > 0 ? (
                            <>
                                <AlertTriangle className="h-6 w-6 text-indigo-600" />
                                {title || "Sincronización con IMPI - Aprobación Requerida"}
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="h-6 w-6 text-green-500" />
                                {title || "Sincronización con IMPI - Revisar Cambios"}
                            </>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {criticalCount > 0 ? (
                            <>
                                Se han detectado cambios que requieren tu aprobación manual.
                                <div className="mt-2 flex gap-4 text-sm">
                                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                                        {criticalCount} Críticos
                                    </Badge>
                                    <div className="text-gray-500">
                                        Auto-aplicables: {summary.added} agregados, {summary.filled} rellenados.
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                Se detectaron cambios que se aplicarán automáticamente al confirmar.
                                <div className="mt-2 flex gap-4 text-sm">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                        {summary.added + summary.filled} cambios automáticos
                                    </Badge>
                                    <div className="text-gray-500">
                                        {summary.added} agregados, {summary.filled} rellenados.
                                    </div>
                                </div>
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {criticalCount > 0 && (
                    <div className="flex justify-end gap-2 my-2">
                        <Button variant="outline" size="sm" onClick={handleRejectAll} className="text-xs">
                            Rechazar Todo
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleAcceptAll} className="text-xs">
                            Aceptar Todo
                        </Button>
                    </div>
                )}

                <div className="max-h-[60vh] overflow-y-auto border rounded-md p-4 bg-gray-50">
                    {items_requires_approval.length === 0 ? (
                        <div className="text-center py-8">
                            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                            <p className="text-gray-700 font-medium mb-2">No hay cambios críticos pendientes</p>
                            <p className="text-sm text-gray-500">
                                Los cambios detectados se aplicarán automáticamente al confirmar.
                            </p>
                            <div className="mt-4 text-sm text-gray-600">
                                <div>✓ {summary.added} movimientos agregados</div>
                                <div>✓ {summary.filled} campos rellenados</div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {items_requires_approval.map((item) => {
                                const decision = decisions[item.id];
                                const isAccepted = decision === 'accepted';
                                const isRejected = decision === 'rejected';

                                return (
                                    <div
                                        key={item.id}
                                        className={cn(
                                            "bg-white border rounded-lg p-4 shadow-sm transition-all",
                                            isAccepted && "border-green-200 bg-green-50/30",
                                            isRejected && "border-red-200 bg-red-50/30"
                                        )}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="secondary" className="font-mono text-xs">
                                                        {item.entity_type.toUpperCase()}
                                                    </Badge>
                                                    <span className="font-medium text-sm text-gray-700">
                                                        Campo: <span className="font-bold text-gray-900">{item.field}</span>
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-400 mt-1">
                                                    ID: {item.entity_key || 'Principal'}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1">
                                                <Button
                                                    size="sm"
                                                    variant={isRejected ? "destructive" : "outline"}
                                                    className={cn("h-8 px-3", isRejected ? "" : "hover:bg-red-50 hover:text-red-600")}
                                                    onClick={() => handleDecision(item.id, 'rejected')}
                                                >
                                                    <X className="h-4 w-4 mr-1" />
                                                    Rechazar
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant={isAccepted ? "default" : "outline"}
                                                    className={cn(
                                                        "h-8 px-3",
                                                        isAccepted ? "bg-green-600 hover:bg-green-700" : "hover:bg-green-50 hover:text-green-600 border-green-200"
                                                    )}
                                                    onClick={() => handleDecision(item.id, 'accepted')}
                                                >
                                                    <Check className="h-4 w-4 mr-1" />
                                                    Aceptar
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
                                            <div className="bg-red-50 border border-red-100 rounded p-2 text-sm">
                                                <div className="text-xs text-red-400 font-semibold mb-1 uppercase">Valor Actual</div>
                                                <div className="font-mono break-all text-red-900">
                                                    {formatValue(item.old_value)}
                                                </div>
                                            </div>

                                            <div className="text-gray-400">
                                                <ArrowRight className="h-5 w-5" />
                                            </div>

                                            <div className="bg-green-50 border border-green-100 rounded p-2 text-sm">
                                                <div className="text-xs text-green-600 font-semibold mb-1 uppercase">Nuevo Valor (IMPI)</div>
                                                <div className="font-mono break-all text-green-900">
                                                    {formatValue(item.new_value)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Sección de Cambios Automáticos */}
                    {items_auto_apply && items_auto_apply.length > 0 && (
                        <div className="mt-4 border-t pt-4">
                            <button
                                onClick={() => setShowAutoChanges(!showAutoChanges)}
                                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 w-full transition-colors"
                            >
                                {showAutoChanges ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                                <span>
                                    {items_auto_apply.length} cambios automáticos
                                    {summary.added > 0 && summary.filled > 0 && ` (${summary.added} agregados, ${summary.filled} rellenados)`}
                                    {summary.added > 0 && !summary.filled && ` (${summary.added} agregados)`}
                                    {!summary.added && summary.filled > 0 && ` (${summary.filled} rellenados)`}
                                </span>
                            </button>

                            {showAutoChanges && (
                                <div className="space-y-2 mt-3 max-h-64 overflow-y-auto pr-2">
                                    {items_auto_apply.map((item) => (
                                        <div
                                            key={item.id}
                                            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                                        >
                                            <div className="flex items-start gap-2 mb-2">
                                                <Badge variant="secondary" className="text-xs">
                                                    {item.entity_type === 'movement' && 'Movimiento'}
                                                    {item.entity_type === 'trademark' && 'Marca'}
                                                    {item.entity_type === 'holder' && 'Titular'}
                                                    {item.entity_type === 'attorney' && 'Apoderado'}
                                                </Badge>
                                                <span className="text-xs text-gray-600">
                                                    {item.field}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className={`text-xs ml-auto ${item.change_type === 'added' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : ''}`}
                                                >
                                                    {item.change_type === 'added' && '+ Agregado'}
                                                    {item.change_type === 'filled' && '✓ Rellenado'}
                                                    {item.change_type === 'changed' && '↔ Modificado'}
                                                </Badge>
                                            </div>

                                            <div className="grid grid-cols-1 gap-2">
                                                {item.old_value && (
                                                    <div className="bg-white p-2 rounded border text-xs">
                                                        <div className="text-gray-500 mb-1">Anterior:</div>
                                                        <div className="text-gray-700 font-mono break-all">
                                                            {formatValue(item.old_value)}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="bg-green-50 p-2 rounded border border-green-200 text-xs">
                                                    <div className="text-green-600 mb-1">Nuevo:</div>
                                                    <div className="text-green-900 font-mono break-all">
                                                        {formatValue(item.new_value)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="mt-4 gap-2 sm:gap-0">
                    <div className="flex-1 flex items-center text-sm text-gray-500">
                        {pendingCount > 0 && (
                            <span className="flex items-center text-indigo-600">
                                <AlertTriangle className="h-4 w-4 mr-1" />
                                {pendingCount} pendientes por decidir
                            </span>
                        )}
                    </div>
                    <Button variant="ghost" onClick={() => onClose(false)} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={loading || (criticalCount > 0 && pendingCount > 0)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? "Aplicando..." : (confirmLabel || (criticalCount > 0 ? "Confirmar Sincronización" : "Aplicar Cambios Automáticos"))}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
